import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ProductCard, ProductCardProps } from './ProductCard';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface Product {
  _id?: string;
  name: string;
  description?: string;
  price: number;
  salePrice?: number;
  currency?: string;
  primaryImage?: string;
  images?: Array<{ url: string; isPrimary?: boolean }>;
  availability?: 'in_stock' | 'out_of_stock' | 'preorder' | 'backorder';
  inventoryQuantity?: number;
  hasVariants?: boolean;
  sku?: string;
  brand?: string;
}

export interface ProductListProps {
  products: Product[];
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onProductPress?: (productId: string) => void;
  onProductEdit?: (productId: string) => void;
  onProductDelete?: (productId: string) => void;
  showActions?: boolean;
  layout?: 'list' | 'grid';
  emptyMessage?: string;
  containerStyle?: any;
  /** Multi-select mode (bulk "Add to Vendor"/"Add to Category" actions) —
   * see ProductCard for the per-item behavior this threads through. */
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (productId: string) => void;
}

export const ProductList: React.FC<ProductListProps> = ({
  products,
  loading = false,
  refreshing = false,
  onRefresh,
  onLoadMore,
  hasMore = false,
  onProductPress,
  onProductEdit,
  onProductDelete,
  showActions = true,
  layout = 'list',
  emptyMessage = 'No products found',
  containerStyle,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const [layoutMode, setLayoutMode] = useState<'list' | 'grid'>(layout);

  const renderProduct = ({ item, index }: { item: Product; index: number }) => {
    const isSelected = !!item._id && !!selectedIds?.includes(item._id);
    if (layoutMode === 'grid') {
      return (
        <View style={styles.gridItem}>
          <ProductCard
            product={item}
            onPress={onProductPress}
            onEdit={onProductEdit}
            onDelete={onProductDelete}
            showActions={showActions}
            containerStyle={styles.gridCard}
            selectionMode={selectionMode}
            isSelected={isSelected}
            onToggleSelect={onToggleSelect}
          />
        </View>
      );
    }

    return (
      <ProductCard
        product={item}
        onPress={onProductPress}
        onEdit={onProductEdit}
        onDelete={onProductDelete}
        showActions={showActions}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
      />
    );
  };

  const renderFooter = () => {
    if (!loading || !hasMore) return null;

    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
        <Text style={styles.footerText}>Loading more products...</Text>
      </View>
    );
  };

  const renderEmpty = () => {
    if (loading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.emptyText}>Loading products...</Text>
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="cube-outline" size={64} color={colors.textLight} />
        <Text style={styles.emptyText}>{emptyMessage}</Text>
        <Text style={styles.emptySubtext}>
          Add your first product to get started
        </Text>
      </View>
    );
  };

  const renderHeader = () => {
    return null; // Removed header - it's redundant
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <FlatList
        data={products}
        renderItem={renderProduct}
        keyExtractor={(item, index) => item._id || `product-${index}`}
        key={layoutMode} // Force re-render on layout change
        numColumns={layoutMode === 'grid' ? 2 : 1}
        ListHeaderComponent={renderHeader}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={renderFooter}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          ) : undefined
        }
        onEndReached={hasMore && onLoadMore ? onLoadMore : undefined}
        onEndReachedThreshold={0.5}
        contentContainerStyle={[
          products.length === 0 ? styles.emptyListContent : styles.listContent,
          containerStyle,
        ]}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.sm,
  },
  headerTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  headerActions: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  layoutButton: {
    width: 36,
    height: 36,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    justifyContent: 'center',
    alignItems: 'center',
  },
  layoutButtonActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primaryTransparent,
  },
  listContent: {
    padding: spacing.md,
  },
  emptyListContent: {
    flexGrow: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
    minHeight: 300,
  },
  emptyText: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.md,
    textAlign: 'center',
    fontFamily: typography.fontFamily.semibold,
  },
  emptySubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    marginTop: spacing.xs,
    textAlign: 'center',
    fontFamily: typography.fontFamily.primary,
  },
  gridItem: {
    flex: 1,
    margin: spacing.xs,
  },
  gridCard: {
    marginBottom: 0,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.md,
    gap: spacing.sm,
  },
  footerText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});

